import React, { type ReactElement, useState } from 'react';

import classnames from 'classnames';
import { useInView } from 'react-intersection-observer';

import { type SizeType, useSize } from '../../features/config';
import { Skeleton } from '../Skeleton';

import { getAvatarColorByInitials, makeInitials } from './Avatar.utils';
import externalUserAvatar from './external-user.svg';

import styles from './Avatar.module.scss';

const NAME_INITIALS_STYLES = [
    styles['initialsContainer--1'],
    styles['initialsContainer--2'],
    styles['initialsContainer--3'],
    styles['initialsContainer--4'],
    styles['initialsContainer--5'],
    styles['initialsContainer--6'],
    styles['initialsContainer--7'],
    styles['initialsContainer--8']
];

export type AvatarProps = {
    /** Optional class names for the wrapper */
    className?: string;
    /** The size of the avatar. */
    size?: SizeType;
    /**
     * The type of avatar to display.
     */
    type?: 'human' | 'initials' | 'external';
    /** The image URL or base64 string. */
    image?: string;
    /**
     * When true, render a skeleton / loading state instead of avatar content.
     * When false, render the avatar content.
     */
    loading?: boolean;
    /**
     * A short text alternative describing the image or icon.
     * For icons (e.g., external) this can be an accessible label.
     */
    alt?: string;
};

export const Avatar = ({
    className,
    size: sizeProp,
    type = 'human',
    image,
    loading = false,
    alt,
    ...rest
}: AvatarProps): ReactElement => {
    const [isImageLoaded, setIsImageLoaded] = useState(false);

    const { inView, ref: inViewRef } = useInView({ triggerOnce: true, delay: 500 });

    const withAlt = alt && alt?.trim().length > 0;

    if (type === 'initials' && !withAlt) {
        throw new Error('`initials` type requires an `alt` prop');
    }

    const avatarInitials = withAlt ? makeInitials(alt) : null;
    const shouldRenderInitials = type === 'initials' || (Boolean(avatarInitials) && !image);
    const avatarColorInitials = getAvatarColorByInitials(
        NAME_INITIALS_STYLES,
        avatarInitials as string
    );
    const size = useSize(sizeProp, 'medium', 'Avatar');

    const wrapperClassName = classnames(
        styles.wrapper,
        {
            [styles.initials]: shouldRenderInitials,
            'chromatic-ignore': true
        },
        styles[size],
        className
    );

    const handleOnLoad = () => {
        setIsImageLoaded(true);
    };

    const handleOnError = (e: React.SyntheticEvent<HTMLImageElement>) => {
        setIsImageLoaded(false);
        const target = e.target as HTMLImageElement;
        target.src = externalUserAvatar;
    };

    const getElement = () => {
        if (loading) {
            return <Skeleton.Avatar size={size} />;
        }

        if (type === 'external') {
            return (
                <img
                    className={styles.image}
                    alt={withAlt ? alt : 'external user'}
                    src={externalUserAvatar}
                    loading="lazy"
                />
            );
        }

        if (shouldRenderInitials) {
            return (
                <div
                    className={classnames(styles.initialsContainer, avatarColorInitials)}
                    role="img"
                    aria-label={alt}
                >
                    {avatarInitials}
                </div>
            );
        }

        return (
            <>
                {!isImageLoaded && <Skeleton.Avatar size={size} />}
                <img
                    className={styles.image}
                    alt={alt}
                    src={image ?? externalUserAvatar}
                    loading="lazy"
                    onLoad={handleOnLoad}
                    onError={handleOnError}
                />
            </>
        );
    };

    return (
        <div className={wrapperClassName} ref={inViewRef}>
            <div className={styles.avatarElement} {...rest}>
                {inView ? getElement() : <Skeleton.Avatar size={size} />}
            </div>
        </div>
    );
};
